import * as React from "react";
import { Star, MapPin, Quote } from "lucide-react";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { Card, CardContent, CardFooter, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const testimonials = [{
  name: "Sarah Jenkins",
  suburb: "North Ward",
  projectType: "Interior Refresh",
  rating: 5,
  text: "The team transformed our Queenslander with such respect for its heritage. The colour consultation was spot on, and the finish is absolutely flawless. It feels like a brand new home."
}, {
  name: "Michael & Jenny",
  suburb: "Castle Hill",
  projectType: "Full Exterior",
  rating: 5,
  text: "Living on the hill means our paint takes a beating from the sun. ANV Painting used premium weather-resistant coatings that look incredible. Their preparation work was meticulous."
}, {
  name: "Bayside Cafe",
  suburb: "The Strand",
  projectType: "Commercial Decor",
  rating: 5,
  text: "We needed a fresh look without disrupting our business hours. The crew was professional, tidy, and worked around our schedule. Our customers love the new vibrant atmosphere."
}, {
  name: "David Thorne",
  suburb: "Annandale",
  projectType: "Roof Restoration",
  rating: 5,
  text: "I was worried about the cost of a full roof restoration, but the value ANV provided was exceptional. They identified issues I hadn't seen and fixed them before painting."
}, {
  name: "Emma Liu",
  suburb: "Douglas",
  projectType: "Deck & Timber",
  rating: 5,
  text: "Our timber deck was grey and weathered. They sanded it back and applied a stunning stain that brings out the natural grain. It's now our favourite spot in the house."
}, {
  name: "Greg Norman",
  suburb: "Kirwan",
  projectType: "Rental Repaint",
  rating: 5,
  text: "Fast, reliable, and high-quality work. I needed a rental property painted between tenants, and they delivered on time and on budget. Highly recommended for landlords."
}];
export default function LocalSuccessStories() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section id="local-success-stories" ref={ref} className="relative w-full py-24 overflow-hidden bg-background">
      {}
      <div className="absolute inset-0 pointer-events-none opacity-[0.03] mix-blend-multiply">
        <div className="absolute inset-0 bg-[url('https://wpvc-images.s3.us-east-1.amazonaws.com/images/1559587/img/townsville_topo_map_watermark.png')] bg-cover bg-center bg-no-repeat grayscale" />
      </div>

      <div className="container relative mx-auto px-4 md:px-6">
        <div className={`max-w-3xl mx-auto text-center mb-16 transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-foreground mb-4">
            Trusted by Townsville Locals
          </h2>
          <p className="text-lg text-muted-foreground">
            From heritage homes in North Ward to modern commercial spaces on The
            Strand, we take pride in delivering excellence across the North
            Queensland region.
          </p>
        </div>

        <div className={`transition-all duration-1000 delay-200 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <Carousel opts={{
          align: "start",
          loop: true
        }} className="w-full max-w-7xl mx-auto">
            <CarouselContent className="-ml-4 md:-ml-6 pb-4">
              {testimonials.map((testimonial, index) => <CarouselItem key={index} className="pl-4 md:pl-6 md:basis-1/2 lg:basis-1/3 h-auto" data-index={index}>
                  <div className="h-full pt-2 px-1 pb-2">
                    <Card className="group h-full flex flex-col border-border/60 bg-card/80 backdrop-blur-sm shadow-sm hover:shadow-[0_0_20px_-5px_var(--color-primary)] hover:border-primary/50 transition-all duration-300 ease-out">
                      <CardHeader className="pb-4">
                        <div className="flex justify-between items-start w-full">
                          <div className="flex flex-col">
                            <h3 className="font-semibold text-lg text-foreground group-hover:text-primary transition-colors duration-300">
                              {testimonial.name}
                            </h3>
                            <div className="flex items-center gap-1 text-sm text-muted-foreground mt-1">
                              <MapPin className="size-3.5" />
                              <span>{testimonial.suburb}</span>
                            </div>
                          </div>
                          <Quote className="size-8 text-primary/20 group-hover:text-primary/40 transition-colors duration-300" />
                        </div>
                      </CardHeader>
                      <CardContent className="flex-grow">
                        <div className="flex gap-0.5 mb-4">
                          {[...Array(5)].map((_, i) => <Star key={i} className={`size-4 ${i < testimonial.rating ? "fill-primary text-primary" : "fill-muted text-muted-foreground"}`} />)}
                        </div>
                        <p className="text-muted-foreground leading-relaxed italic">
                          &quot;{testimonial.text}&quot;
                        </p>
                      </CardContent>
                      <CardFooter className="pt-4 border-t border-border/30 mt-auto">
                        <Badge variant="secondary" className="bg-secondary/50 hover:bg-secondary/70 text-secondary-foreground font-medium tracking-wide">
                          {testimonial.projectType}
                        </Badge>
                      </CardFooter>
                    </Card>
                  </div>
                </CarouselItem>)}
            </CarouselContent>
            <div className="hidden md:block">
              <CarouselPrevious className="-left-12 border-primary/20 hover:bg-primary hover:text-primary-foreground hover:border-primary transition-colors" />
              <CarouselNext className="-right-12 border-primary/20 hover:bg-primary hover:text-primary-foreground hover:border-primary transition-colors" />
            </div>
            <div className="flex justify-center gap-4 mt-8 md:hidden">
              <CarouselPrevious className="static translate-y-0 translate-x-0 border-primary/20 hover:bg-primary hover:text-primary-foreground" />
              <CarouselNext className="static translate-y-0 translate-x-0 border-primary/20 hover:bg-primary hover:text-primary-foreground" />
            </div>
          </Carousel>
        </div>
      </div>
    </section>;
}